/**
 * Driving record + medical examiner's certificate ATTESTATION (Part C7, 2026-10-08).
 *
 * Behind the runtime feature flag `driving_record_attestation` (featureFlags/global). It stays OFF on
 * production until a lawyer has reviewed it. An absent flag is OFF everywhere (useFeatureFlag returns
 * its default false for an unknown key; the server treats anything but state 'on' as off).
 *
 * What the EMT states (never an upload, never a document):
 *   profiles/{uid}.drivingRecord        { points: 0-99, duiLast5Years: boolean, updatedAt }
 *   profiles/{uid}.medicalExaminerCert  { has: boolean, expiresAt: 'YYYY-MM-DD' | null, updatedAt }
 *
 * Privacy: these live ONLY on the private profiles/{uid} (readable by the owner and admins; an employer
 * can read only same-org EMPLOYER profiles, never an EMT's). Neither publicProfiles writer
 * (src/lib/firestore/publicProfiles.ts, functions/src/readinessSync.ts) copies them: both build an
 * explicit field list. An employer sees them only through the getCandidateSensitiveInfo callable
 * (functions/src/candidateSensitiveInfo.ts), which checks the same relationship as
 * getCandidateDocumentUrl: the candidate applied to the employer's organization, or is on its team
 * (workforce status active / onboarding / separated).
 *
 * Requisition setting: jobs/{jobId}.drivingPolicy = { considerRecentDui: false } when the employer
 * answers "No" to "Consider candidates with a DUI in the last 5 years?". Absent = yes (the default).
 * Effect (pending legal review): it does NOT block applying. The candidate sees a plain line on the
 * job page, and the employer sees DUI_FLAG_TEXT on that candidate's application when the candidate
 * reports a DUI in the last 5 years. The server computes that flag (duiConflictForJob in
 * functions/src/candidateSensitiveInfo.ts mirrors reportsDuiConflict below; the offline proof
 * scripts/qa/offline-driving-record.mjs checks the two agree).
 */

export const DRIVING_RECORD_FLAG = 'driving_record_attestation';
export const DRIVING_RECORD_FLAG_LABEL = 'Driving record and medical certificate attestation';
export const DRIVING_RECORD_FLAG_DESCRIPTION =
  'EMTs state license points, a DUI in the last 5 years, and whether they hold a DOT medical card. Shown only to employers they apply to or work for. Keep off on production until legal review.';

export const DRIVING_POINTS_MAX = 99;
/** A medical examiner's certificate lasts at most 2 years; allow some slack, refuse obvious typos. */
export const MED_CERT_MAX_YEARS_AHEAD = 5;

export const DRIVING_RECORD_VISIBILITY_NOTE = 'Only employers you apply to or work for can see this.';
export const DUI_FLAG_TEXT =
  'Driving record: reports a DUI in the last 5 years; this job does not consider that.';
export const JOB_NO_RECENT_DUI_NOTE = 'This employer does not consider candidates with a DUI in the last 5 years.';

export type DrivingRecord = {
  points: number;
  duiLast5Years: boolean;
  updatedAt?: unknown;
};

export type MedicalExaminerCert = {
  has: boolean;
  /** 'YYYY-MM-DD' when has, else null. */
  expiresAt: string | null;
  updatedAt?: unknown;
};

export type JobDrivingPolicy = { considerRecentDui: boolean };

// ---------------------------------------------------------------------------------------------
// Flag decision
// ---------------------------------------------------------------------------------------------

/**
 * The SERVER's decision: only state 'on' is on. 'rollout' (per-org opt-in) is treated as off by the
 * callable, so a rollout never exposes data the platform-wide switch has not approved. The client
 * hook (useFeatureFlag) does honor rollout overrides for employer UI, but the card stays empty because
 * the callable answers { enabled: false }. EMTs have no org, so rollout is off for them too.
 */
export function isDrivingRecordFlagOnForServer(state: unknown): boolean {
  return state === 'on';
}

// ---------------------------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------------------------

/** Today's LOCAL calendar date as YYYY-MM-DD (not UTC: a late-evening save must not shift a day). */
export function localTodayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** A real calendar date in YYYY-MM-DD form (rejects 2026-02-30). */
export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function addYearsIso(iso: string, years: number): string {
  const [y, m, d] = iso.split('-');
  return `${Number(y) + years}-${m}-${d}`;
}

// ---------------------------------------------------------------------------------------------
// Validation (the /account form)
// ---------------------------------------------------------------------------------------------

export type FieldResult<T> = { ok: true; value: T } | { ok: false; message: string };

/** Points on the license: whole number 0-99. Blank is "not answered" (value null). */
export function parseDrivingPoints(raw: string): FieldResult<number | null> {
  const s = (raw ?? '').trim();
  if (s === '') return { ok: true, value: null };
  if (!/^\d{1,2}$/.test(s)) {
    return { ok: false, message: `Enter points as a whole number from 0 to ${DRIVING_POINTS_MAX}.` };
  }
  const n = Number(s);
  if (n < 0 || n > DRIVING_POINTS_MAX) {
    return { ok: false, message: `Enter points as a whole number from 0 to ${DRIVING_POINTS_MAX}.` };
  }
  return { ok: true, value: n };
}

/** Expiry of a certificate the EMT says they have: a real date, today or later, within 5 years. */
export function validateMedCertExpiry(expiresAt: string, todayIso: string): FieldResult<string> {
  const s = (expiresAt ?? '').trim();
  if (!s) return { ok: false, message: 'Enter the expiry date on your DOT medical card.' };
  if (!isIsoDate(s)) return { ok: false, message: 'Enter a real date for the expiry.' };
  if (s < todayIso) {
    return { ok: false, message: 'That date has passed. If your certificate has expired, choose No.' };
  }
  if (s > addYearsIso(todayIso, MED_CERT_MAX_YEARS_AHEAD)) {
    return { ok: false, message: `The expiry date must be within ${MED_CERT_MAX_YEARS_AHEAD} years.` };
  }
  return { ok: true, value: s };
}

export type DrivingRecordFormState = {
  points: string;
  /** null = not answered. */
  duiLast5Years: boolean | null;
  /** null = not answered. */
  medCertHas: boolean | null;
  medCertExpiresAt: string;
};

export type DrivingRecordFormResult =
  | {
      ok: true;
      /** null = clear the stored value (the EMT left that half blank). */
      drivingRecord: Omit<DrivingRecord, 'updatedAt'> | null;
      medicalExaminerCert: Omit<MedicalExaminerCert, 'updatedAt'> | null;
    }
  | { ok: false; field: 'points' | 'dui' | 'medCertExpiry'; message: string };

/**
 * Each half is optional, but a half that is started must be finished: points and the DUI answer go
 * together; "Yes" to the certificate needs its expiry date.
 */
export function validateDrivingRecordForm(form: DrivingRecordFormState, todayIso: string): DrivingRecordFormResult {
  const points = parseDrivingPoints(form.points);
  if (!points.ok) return { ok: false, field: 'points', message: points.message };

  let drivingRecord: Omit<DrivingRecord, 'updatedAt'> | null = null;
  const pointsGiven = points.value !== null;
  const duiGiven = form.duiLast5Years !== null;
  if (pointsGiven && !duiGiven) {
    return { ok: false, field: 'dui', message: 'Answer the DUI question, or clear the points.' };
  }
  if (!pointsGiven && duiGiven) {
    return { ok: false, field: 'points', message: 'Enter the points on your license (0 if none).' };
  }
  if (pointsGiven && duiGiven) {
    drivingRecord = { points: points.value as number, duiLast5Years: form.duiLast5Years === true };
  }

  let medicalExaminerCert: Omit<MedicalExaminerCert, 'updatedAt'> | null = null;
  if (form.medCertHas === true) {
    const expiry = validateMedCertExpiry(form.medCertExpiresAt, todayIso);
    if (!expiry.ok) return { ok: false, field: 'medCertExpiry', message: expiry.message };
    medicalExaminerCert = { has: true, expiresAt: expiry.value };
  } else if (form.medCertHas === false) {
    medicalExaminerCert = { has: false, expiresAt: null };
  }

  return { ok: true, drivingRecord, medicalExaminerCert };
}

// ---------------------------------------------------------------------------------------------
// Reading stored values (profile fields are owner-written: never trust their shape)
// ---------------------------------------------------------------------------------------------

export function readDrivingRecord(raw: unknown): DrivingRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const points = r.points;
  if (typeof points !== 'number' || !Number.isInteger(points) || points < 0 || points > DRIVING_POINTS_MAX) return null;
  if (typeof r.duiLast5Years !== 'boolean') return null;
  return { points, duiLast5Years: r.duiLast5Years, updatedAt: r.updatedAt };
}

export function readMedicalExaminerCert(raw: unknown): MedicalExaminerCert | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.has === false) return { has: false, expiresAt: null, updatedAt: r.updatedAt };
  if (r.has === true && isIsoDate(r.expiresAt)) return { has: true, expiresAt: r.expiresAt, updatedAt: r.updatedAt };
  return null;
}

/** Expired = the stated expiry is before today (local date). */
export function isMedCertExpired(cert: Pick<MedicalExaminerCert, 'has' | 'expiresAt'> | null, todayIso: string): boolean {
  return Boolean(cert?.has && cert.expiresAt && cert.expiresAt < todayIso);
}

// ---------------------------------------------------------------------------------------------
// The requisition setting and the "flag to employer" rule
// ---------------------------------------------------------------------------------------------

/** Default YES: only an explicit { considerRecentDui: false } on the job says no. */
export function jobConsidersRecentDui(job: { drivingPolicy?: unknown } | null | undefined): boolean {
  const p = job?.drivingPolicy as { considerRecentDui?: unknown } | null | undefined;
  return !(p && typeof p === 'object' && p.considerRecentDui === false);
}

/**
 * Flag the application to the employer when the job says no to a recent DUI AND the candidate states
 * one. Never blocks the application (legal review pending). Unknown / unanswered = no flag.
 */
export function reportsDuiConflict(
  job: { drivingPolicy?: unknown } | null | undefined,
  record: Pick<DrivingRecord, 'duiLast5Years'> | null | undefined
): boolean {
  return !jobConsidersRecentDui(job) && record?.duiLast5Years === true;
}

export function formatIsoDateForDisplay(iso: string): string {
  if (!isIsoDate(iso)) return iso;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
